/**
 * @fileoverview Pure builder: schema ElementAnimation → CSS class + custom
 * properties. Mirrors web-shell/engine/transitions/transitionSpec.ts:
 * no DOM access, `null` ALWAYS means "no animation" (element renders in its
 * final state).
 * @module screen-view-v2/animations/elementAnimationSpec
 */

import type {
  ElementAnimation,
  StaggerConfig,
  AnimatableProperties,
} from '@segmently/schema-models/v2';
import { resolveEasing, SPRING_FALLBACK_DURATION_MS } from './easing';
import { resolvePreset, presetClassName, type PresetCategory } from './presetRegistry';
import { computeStaggerDelay } from './stagger';

// ============================================================================
// Types
// ============================================================================

/**
 * Triggers implemented in v1. Everything else is dropped with a warn.
 * `onValueChange` was pruned — no hook consumes it, so specs built for it
 * silently never played; it now degrades through the same warn path as
 * other unimplemented schema triggers.
 */
export type SupportedTrigger =
  | 'onAppear'
  | 'afterDelay'
  | 'onScreenEnter'
  | 'onSelectionChange'
  | 'onTap';

export interface ResolvedElementAnimationSpec {
  /** Deterministic global class: `sv2-anim-<preset>` or `sv2-anim-custom` */
  className: string;
  /** Inline CSS custom properties (timing + custom from/to values) */
  vars: Record<string, string>;
  durationMs: number;
  delayMs: number;
  /** 'infinite' or a positive integer as string */
  iterationCount: string;
  category: PresetCategory;
  trigger: SupportedTrigger;
}

export interface BuildElementAnimationOptions {
  reducedMotion: boolean;
  /** 0-based display position for stagger math */
  staggerIndex?: number;
  stagger?: StaggerConfig | null;
  /** Contextual trigger when animation.trigger is unset (e.g. selectionAnimation → onSelectionChange) */
  defaultTrigger?: SupportedTrigger;
  /** Force infinite loop semantics (idleAnimation) */
  forceLoop?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_DURATION_MS = 300;
const MAX_DURATION_MS = 10_000;

const SUPPORTED_TRIGGERS = new Set<string>([
  'onAppear', 'afterDelay', 'onScreenEnter', 'onSelectionChange', 'onTap',
]);

/** Custom-mode animatable subset (transform + opacity). */
const CUSTOM_PROPERTY_VARS: Record<string, { fromVar: string; toVar: string; unit: string }> = {
  x: { fromVar: '--sv2-from-x', toVar: '--sv2-to-x', unit: 'px' },
  y: { fromVar: '--sv2-from-y', toVar: '--sv2-to-y', unit: 'px' },
  scale: { fromVar: '--sv2-from-scale', toVar: '--sv2-to-scale', unit: '' },
  rotate: { fromVar: '--sv2-from-rotate', toVar: '--sv2-to-rotate', unit: 'deg' },
  opacity: { fromVar: '--sv2-from-opacity', toVar: '--sv2-to-opacity', unit: '' },
};

// Warn once per value — the builder runs on every render of animated screens
const warnedTriggers = new Set<string>();
const warnedCustomProps = new Set<string>();
let warnedKeyframesDegraded = false;
let warnedRepeatDelay = false;

// ============================================================================
// Internals
// ============================================================================

function resolveTrigger(
  animation: ElementAnimation,
  defaultTrigger: SupportedTrigger,
): SupportedTrigger | null {
  const raw = (animation.trigger as string | undefined) ?? defaultTrigger;
  if (SUPPORTED_TRIGGERS.has(raw)) return raw as SupportedTrigger;
  if (!warnedTriggers.has(raw)) {
    warnedTriggers.add(raw);
    console.warn(`[ElementAnimation] Trigger "${raw}" is not supported yet — skipping animation`);
  }
  return null;
}

/** Emit --sv2-from-* / --sv2-to-* vars for the supported property subset. */
function customModeVars(
  from: AnimatableProperties | null | undefined,
  to: AnimatableProperties | null | undefined,
): Record<string, string> | null {
  const vars: Record<string, string> = {};
  let hasAny = false;
  const apply = (props: AnimatableProperties | null | undefined, side: 'fromVar' | 'toVar') => {
    if (!props) return;
    for (const [key, value] of Object.entries(props)) {
      if (value == null) continue;
      const mapping = CUSTOM_PROPERTY_VARS[key];
      if (!mapping) {
        if (!warnedCustomProps.has(key)) {
          warnedCustomProps.add(key);
          console.warn(`[ElementAnimation] Animatable property "${key}" is not supported in custom mode — ignoring`);
        }
        continue;
      }
      vars[mapping[side]] = `${value}${mapping.unit}`;
      hasAny = true;
    }
  };
  apply(from, 'fromVar');
  apply(to, 'toVar');
  return hasAny ? vars : null;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Build the CSS spec for one element animation, or `null` for "no animation".
 *
 * `null` cases: reducedMotion, disabled, unsupported trigger, unknown preset,
 * empty/unsupported custom properties, non-positive duration.
 *
 * Mode priority: preset > keyframes (first/last step) > from/to.
 */
export function buildElementAnimationSpec(
  animation: ElementAnimation | null | undefined,
  options: BuildElementAnimationOptions,
): ResolvedElementAnimationSpec | null {
  if (!animation || animation.disabled) return null;
  if (options.reducedMotion) return null;

  const trigger = resolveTrigger(animation, options.defaultTrigger ?? 'onAppear');
  if (trigger === null) return null;

  // ── Mode resolution ──────────────────────────────────────────
  let className: string;
  let category: PresetCategory;
  let presetDefaultDurationMs = DEFAULT_DURATION_MS;
  let presetDefaultEasing: string | undefined;
  const vars: Record<string, string> = {};

  if (animation.preset) {
    const def = resolvePreset(animation.preset as string);
    if (!def) return null;
    className = presetClassName(animation.preset as string);
    category = def.category;
    presetDefaultDurationMs = def.defaultDurationMs;
    presetDefaultEasing = def.defaultEasing;
  } else {
    let from = animation.from;
    let to = animation.to;
    const keyframes = animation.keyframes;
    if (keyframes && keyframes.length >= 2) {
      if (keyframes.length > 2 && !warnedKeyframesDegraded) {
        warnedKeyframesDegraded = true;
        console.warn('[ElementAnimation] Multi-step keyframes are degraded to first/last steps on web');
      }
      const sorted = [...keyframes].sort((a, b) => (a.offset ?? 0) - (b.offset ?? 0));
      from = sorted[0]?.properties ?? from;
      to = sorted[sorted.length - 1]?.properties ?? to;
    }
    const customVars = customModeVars(from, to);
    if (!customVars) return null;
    Object.assign(vars, customVars);
    className = 'sv2-anim-custom';
    category = 'entrance';
  }

  if (options.forceLoop) {
    category = 'loop';
  }

  // ── Timing ───────────────────────────────────────────────────
  const timing = animation.timing;
  const isSpring = timing?.type === 'spring';
  const rawDuration = isSpring
    ? SPRING_FALLBACK_DURATION_MS
    : timing?.duration ?? presetDefaultDurationMs;
  if (rawDuration <= 0) return null;
  const durationMs = Math.min(rawDuration, MAX_DURATION_MS);

  const triggerDelay = trigger === 'afterDelay' ? Math.max(animation.triggerDelay ?? 0, 0) : 0;
  const staggerDelay = computeStaggerDelay(options.staggerIndex ?? 0, options.stagger);
  const delayMs = Math.max(timing?.delay ?? 0, 0) + triggerDelay + staggerDelay;

  const easing = presetDefaultEasing && !timing?.easing && !isSpring
    ? presetDefaultEasing
    : resolveEasing(timing);

  // ── Repeat ───────────────────────────────────────────────────
  const repeat = animation.repeat;
  let iterationCount: string;
  if (repeat?.count === -1 || (category === 'loop' && repeat?.count == null)) {
    iterationCount = 'infinite';
  } else {
    iterationCount = String(Math.max(repeat?.count ?? 0, 0) + 1);
  }
  if (repeat?.delay && !warnedRepeatDelay) {
    warnedRepeatDelay = true;
    console.warn('[ElementAnimation] RepeatConfig.delay (pause between repeats) is not supported on web — ignoring');
  }
  const direction = repeat?.reverse ? 'alternate' : 'normal';

  // fillMode: entrance holds the from-state during delay (backwards) so
  // staggered items don't flash visible before their turn.
  const fillMode = (timing?.fillMode as string | undefined)
    ?? (category === 'entrance' ? 'backwards' : 'none');

  vars['--sv2-anim-duration'] = `${durationMs}ms`;
  vars['--sv2-anim-delay'] = `${delayMs}ms`;
  vars['--sv2-anim-easing'] = easing;
  vars['--sv2-anim-iteration'] = iterationCount;
  vars['--sv2-anim-direction'] = direction;
  vars['--sv2-anim-fill'] = fillMode;

  return {
    className: category === 'loop' ? `${className} sv2-anim--loop` : className,
    vars,
    durationMs,
    delayMs,
    iterationCount,
    category,
    trigger,
  };
}
